import { type ReactNode, useState } from 'react';
import {
  RequiredVideo,
  RequiredVideoCaption,
  RequiredVideoPlayer,
} from '@/components/ui';
import { useStartRedemption } from '@/hooks/use-start-redemption';
import { useForfeitRedemption } from '@/hooks/use-forfeit-redemption';
import { RedemptionForfeitModal } from './redemption-forfeit-modal';
import { RulesBlurb, Actions } from './redemption-block-parts';

type CardProps = {
  watched: boolean;
  setWatched: (v: boolean) => void;
  onForfeit: () => void;
};

function CardHeader(): ReactNode {
  return (
    <h2 className="font-display text-lg font-bold text-text-primary">
      ¡Una última oportunidad!
    </h2>
  );
}

function ActionsBlock(p: {
  watched: boolean;
  startPending: boolean;
  onStart: () => void;
  onForfeit: () => void;
}): ReactNode {
  return (
    <div className="space-y-xs">
      <Actions
        watched={p.watched}
        startPending={p.startPending}
        onStart={p.onStart}
        onForfeit={p.onForfeit}
      />
      <RequiredVideoCaption />
    </div>
  );
}

function BlockCard(p: CardProps): ReactNode {
  const start = useStartRedemption();
  return (
    <div className="space-y-base rounded-lg border border-brand-red/30 bg-brand-red/5 p-base">
      <CardHeader />
      <RulesBlurb />
      <RequiredVideo videoKey="redemption" onWatched={() => p.setWatched(true)}>
        <RequiredVideoPlayer />
        <ActionsBlock
          watched={p.watched}
          startPending={start.isPending}
          onStart={() => start.mutate()}
          onForfeit={p.onForfeit}
        />
      </RequiredVideo>
    </div>
  );
}

export function RedemptionBlock(): ReactNode {
  const [watched, setWatched] = useState(false);
  const [forfeitOpen, setForfeitOpen] = useState(false);
  const forfeit = useForfeitRedemption();
  return (
    <>
      <BlockCard
        watched={watched}
        setWatched={setWatched}
        onForfeit={() => setForfeitOpen(true)}
      />
      <RedemptionForfeitModal
        open={forfeitOpen}
        onClose={() => setForfeitOpen(false)}
        onConfirm={() => forfeit.mutate()}
        isPending={forfeit.isPending}
      />
    </>
  );
}
